import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Spinner, Alert, Tab } from "react-bootstrap";
import MSCDItem from "./StaticCovidDataItem_En";
import { TimeSeriesChart, toTimestamp } from "../modeling/components/TimeSeriesChart";

const variants = {
  visible: (custom) => ({ opacity: 1, y: 0, transition: { delay: custom * 0.2 } }),
  hidden: { opacity: 0, y: 100 },
};

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

const dayPoints = (rows, key) =>
  rows.map((r) => [toTimestamp(r.date), toInt(r[key])]).filter((p) => p[0] != null);

const StaticCovidData_En = (props) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pickedDate, setPickedDate] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    axios
      .get(props.region.hr)
      .then((res) => {
        if (!alive) return;
        const data = Array.isArray(res.data) ? res.data : [];
        setRows(data);
        setPickedDate(data.length ? data[data.length - 1].date : "");
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        console.log(err);
        setError(true);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [props.region.hr]);

  const minDate = rows.length ? rows[0].date : undefined;
  const maxDate = rows.length ? rows[rows.length - 1].date : undefined;
  const current = rows.find((r) => r.date === pickedDate);

  const box = (name, key) => {
    if (loading) return { name, loading: true };
    const v = current ? toInt(current[key]) : null;
    return v === null
      ? { name, noData: true, lastDatadate: pickedDate }
      : { name, value: v, lastDatadate: pickedDate };
  };

  const lasts = [
    { id: 1, ...box("infection cases", "new_diagnoses") },
    { id: 2, ...box("recovery cases", "new_recoveries") },
    { id: 3, ...box("fatal outcomes", "new_deaths") },
  ];

  // Two Y-axes: left — large cumulative (cases/recoveries), right — small (daily/deaths).
  const series = [
    { name: "Cumulative cases", data: dayPoints(rows, "cum_diagnoses"), color: "#212529", yAxis: 0 },
    { name: "Cumulative recoveries", data: dayPoints(rows, "cum_recoveries"), color: "#fc8dd6", yAxis: 0 },
    { name: "New daily cases", data: dayPoints(rows, "new_diagnoses"), color: "#00bfff", yAxis: 1 },
    { name: "Cumulative deaths", data: dayPoints(rows, "cum_deaths"), color: "#ff0000", yAxis: 1 },
  ];

  const yAxes = [
    { title: { text: "Cases / recoveries" }, min: 0, startOnTick: false, endOnTick: false },
    { title: { text: "Daily new / deaths" }, min: 0, opposite: true, startOnTick: false, endOnTick: false },
  ];

  return (
    <Tab.Pane eventKey={props.region.eventKey}>
      <div className="mx-3 my-3">
        <div className="mb-3 d-flex align-items-center" style={{ gap: 8 }}>
          <span className="text-muted">As of</span>
          <input
            type="date"
            className="form-control"
            style={{ maxWidth: 190 }}
            min={minDate}
            max={maxDate}
            value={pickedDate}
            onChange={(e) => setPickedDate(e.target.value)}
            disabled={loading}
          />
        </div>
        <Container>
          <Row style={{ width: "100%" }}>
            {lasts.map((item, index) => (
              <MSCDItem
                initial="hidden"
                whileInView="visible"
                viewport={{ amount: 0.05 }}
                custom={index + 1}
                variants={variants}
                key={item.id}
                last={item}
              />
            ))}
          </Row>
        </Container>
        <div className="my-4">
          {loading ? (
            <div style={{ height: 420, position: "relative" }}>
              <Spinner style={{ position: "absolute", top: "45%", left: "50%" }} animation="border" variant="info" />
            </div>
          ) : error ? (
            <Alert variant="danger" className="my-5">
              <Alert.Heading>Loading error</Alert.Heading>
              The server is temporarily unavailable, please refresh the page or try again later.
            </Alert>
          ) : (
            <TimeSeriesChart title="COVID-19 statistics" series={series} yAxis={yAxes} language="en" />
          )}
        </div>
      </div>
    </Tab.Pane>
  );
};

export default StaticCovidData_En;
